/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { TextareaAutosize, Grid, FormHelperText, Stack, Box } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm, FieldValues, Controller } from 'react-hook-form';
import {
  ICandidate,
  uploadFileToS3,
  ICandidateResume,
  IJob,
  newMappingEntity,
  candidateAggregatedDataWithJobId,
  getSubmissionCount,
  IMappingFormSettingsResponse,
} from 'wisestep-api';
import {
  JobPreference,
  LocationInfo,
  PersonalInfo,
  SalaryDetails,
  WorkExpForm,
  WsButton,
  Item,
  WsDrawer,
  ControlledRecruiterActivity,
  IMessageAndInteractionPipeline,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  ChatBoxHeader,
  IUserDTO,
  DocumentInfo,
  AssessmentInfo,
} from 'wisestep-base';

import { useUserContext } from '../../../context';
import { SourceType } from '../../../features/candidates/components/candidateBulkSelectionBar/candidateBulkSelectionBar';
import { isAnyFieldShown } from '../../../features/crmSettings/helper/configMappingFormHelper';
import {
  amplitudeEventJobCrmSmartSourceAiMapAsLeadSave,
  amplitudeEventJobCrmSmartSourceSimilarJobsMapAsLeadSave,
} from '../../../features/jobsCrm/utils/AmplitudeEvents';
import { cacheJob } from '../../../utils/cacheService';
import { success, failed, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { mappingFormSchema } from '../schema/formSchema';
import IOtherDocument from '../types/IOtherDocument';
import { downloadCV, trackCV } from '../utils/cvHelper';
import { submissionFormDefaultValues } from '../utils/fieldDefaultValues';
import { updateMapping, saveMapping, getMappingFailedNotification } from '../utils/saveMapping';

import ActionFooter from './ActionFooter';
import CVViewModal from './CVViewModal';
import WidgetJobManagement from './WidgetJobManagement';

interface IUpdateProfile {
  openUpdatePanel: boolean;
  pipeline: IMessageAndInteractionPipeline;
  candidate: ICandidate | null;
  downloadSource?: string | null;
  closeUpdatePanel: () => void;
  job?: IJob;
  mapAsLead?: boolean;
  showProfile?: boolean;
  hideJobSelection?: boolean;
  isFrom?: string;
  heading?: string;
  mappingFormSettings?: IMappingFormSettingsResponse;
  duplicateMapping?: boolean;
}

const UpdateProfile: FC<IUpdateProfile> = ({
  openUpdatePanel = false,
  pipeline,
  candidate,
  downloadSource,
  closeUpdatePanel,
  job,
  mapAsLead = false,
  showProfile = true,
  hideJobSelection = false,
  isFrom,
  heading,
  mappingFormSettings,
  duplicateMapping = false,
}) => {
  const contactActions =
    candidate?.contactActionsObjs && candidate?.contactActionsObjs.length > 0
      ? candidate?.contactActionsObjs[candidate?.contactActionsObjs.length - 1]?.actions ?? null
      : null;
  const contactResults =
    candidate?.contactActionsObjs && candidate?.contactActionsObjs.length > 0
      ? candidate?.contactActionsObjs[candidate?.contactActionsObjs.length - 1]?.result?.result ??
        null
      : null;
  const isApplication =
    candidate?.candidateStatusTags?.includes('applied') && candidate?.jobId == job?.id;

  const [candidateEntity, setCandidateEntity] = useState<ICandidate | null>(candidate);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openCvView, setOpenCvView] = useState<boolean>(false);
  const [allowReset, setAllowReset] = useState<boolean>(false);
  const [actions, setActions] = useState<string[]>([]);
  const [results, setResults] = useState<string[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const onCandidateUpdate = useConversationWidgetStore((state) => state.onCandidateUpdate);
  const cvViewSourcePage = useConversationWidgetStore((state) => state.cvViewSourcePage);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const openModalWithAction = useConversationWidgetStore((state) => state.openModalWithAction);
  const activityConversation = useConversationWidgetStore((state) => state.activityConversation);
  const [resumeURl, setResumeURL] = useState<string | null>(null);
  const [candidateResumes, setCandidateResumes] = useState<ICandidateResume[]>([]);
  const [otherDocument, setOtherDocument] = useState<IOtherDocument[]>([]);
  let showPersonalInfoSettings = true;
  let showWorkExpSettings = true;
  let showJobPreferenceSettings = true;
  let showSalaryInfoSettings = true;
  let showAssessmentInfoSettings = true;

  if (mappingFormSettings) {
    const showTitlesObj = isAnyFieldShown(mappingFormSettings);
    ({
      showPersonalInfoSettings,
      showWorkExpSettings,
      showJobPreferenceSettings,
      showSalaryInfoSettings,
      showAssessmentInfoSettings,
    } = showTitlesObj);
  }

  const disableMapping = !activityConversation
    ? candidate?.jobId && candidate.jobId === job?.id && candidate?.isJobCandidateMapping === false
    : false;
  const formOptions = {
    resolver: yupResolver(
      mappingFormSchema(mappingFormSettings, candidateEntity?.profileURLs?.[0] ?? '')
    ),
    defaultValues: submissionFormDefaultValues(candidateEntity, job),
  };

  const methods = useForm(formOptions);

  const { handleSubmit, watch, reset, control, register, setValue } = methods;

  useEffect(() => {
    allowReset && reset(submissionFormDefaultValues(candidateEntity, job));
  }, [allowReset, candidateEntity, reset]);

  register('email', {
    onBlur: async (e) => {
      if (!isEmpty(e.target.value)) {
        const jobId = job?.id ?? null;
        const validate = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,6})+$/.test(e.target.value);
        if (validate) {
          await setTimeout(() => setValue('email', e.target.value), 1000);
          const aggregatedData = await candidateAggregatedDataWithJobId(
            {
              email: e.target.value,
              candidateProfileURLs: [],
              linkedInURL: '',
            },
            jobId
          );
          if (aggregatedData?.jobInteraction) {
            setNotification(warning('Candidate is already mapped to this job'));
            closeUpdatePanel();
            if (showProfile) {
              job && cacheJob(job);
              openModalWithAction(
                aggregatedData?.jobInteraction.id,
                'mapping',
                'mapping',
                'mapping'
              );
            }
          } else if (!isEmpty(aggregatedData.candidateEntity)) {
            setAllowReset(true);
            setCandidateEntity(aggregatedData.candidateEntity);
          }
        } else {
          await setTimeout(() => setValue('email', ''), 200);
          setNotification(warning('Please enter valid email'));
        }
      }
    },
  });

  register('passportNumber', {
    onBlur: async (e) => {
      const jobId = job?.id ?? null;
      await setTimeout(() => setValue('passportNumber', e.target.value), 1000);
      const aggregatedData = await candidateAggregatedDataWithJobId(
        {
          email: '',
          candidateProfileURLs: [],
          linkedInURL: '',
          passportNumber: e.target.value,
        },
        jobId
      );
      if (aggregatedData?.jobInteraction) {
        setNotification(warning('Candidate is already mapped to this job'));
        closeUpdatePanel();
        if (showProfile) {
          job && cacheJob(job);
          openModalWithAction(aggregatedData?.jobInteraction.id, 'mapping', 'mapping', 'mapping');
        }
      } else if (!isEmpty(aggregatedData.candidateEntity)) {
        setAllowReset(true);
        setCandidateEntity(aggregatedData.candidateEntity);
      }
    },
  });

  register('phone', {
    onBlur: async (e) => {
      const jobId = job?.id ?? null;
      await setTimeout(() => setValue('phone', e.target.value), 1000);
      const aggregatedData = await candidateAggregatedDataWithJobId(
        {
          email: '',
          candidateProfileURLs: [],
          linkedInURL: '',
          phoneNumber: e.target.value,
        },
        jobId
      );
      if (aggregatedData?.jobInteraction) {
        setNotification(warning('Candidate is already mapped to this job'));
        closeUpdatePanel();
        if (showProfile) {
          job && cacheJob(job);
          openModalWithAction(aggregatedData?.jobInteraction.id, 'mapping', 'mapping', 'mapping');
        }
      } else if (!isEmpty(aggregatedData.candidateEntity)) {
        setAllowReset(true);
        setCandidateEntity(aggregatedData.candidateEntity);
      }
    },
  });

  register('socialLinks', {
    onBlur: async (e) => {
      if (!isEmpty(e.target.value)) {
        const jobId = job?.id ?? null;
        const urls = e.target.value.filter((url) => {
          const pattern = new RegExp(
            '((http|https)://)?(www.)?[a-zA-Z0-9@:%._\\+~#?&//=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%._\\+~#?&//=]*)'
          );
          return pattern.test(url);
        });
        await setTimeout(() => setValue('socialLinks', urls), 1000);
        if (!isEmpty(urls)) {
          const aggregatedData = await candidateAggregatedDataWithJobId(
            {
              email: '',
              candidateProfileURLs: e.target.value,
              linkedInURL: '',
            },
            jobId
          );
          if (aggregatedData?.jobInteraction) {
            setNotification(warning('Candidate is already mapped to this job'));
            closeUpdatePanel();
            if (showProfile) {
              job && cacheJob(job);
              openModalWithAction(
                aggregatedData?.jobInteraction.id,
                'mapping',
                'mapping',
                'mapping'
              );
            }
          } else if (!isEmpty(aggregatedData.candidateEntity)) {
            setAllowReset(true);
            setCandidateEntity(aggregatedData?.candidateEntity);
          }
        } else {
          await setTimeout(() => setValue('socialLinks', ['']), 200);
          setNotification(warning('Please enter valid urls'));
        }
      }
    },
  });

  useEffect(() => {
    if (contactActions) {
      setActions(contactActions);
    }
    if (contactResults) {
      setResults(contactResults);
    }
  }, [contactActions, contactResults]);

  useEffect(() => {
    setCandidateEntity(candidate);
    setOpenDrawer(openUpdatePanel);
  }, [candidate, openUpdatePanel]);

  const onFormSubmit = async (data: FieldValues) => {
    if (mappingFormSettings?.recruiterActivity.mandatory) {
      if (!isEmpty(data.recruiterActivity.actions) || !isEmpty(data.recruiterActivity.results)) {
        setDisableSubmit(true);
        data.source = watch('source');
        data.downloadSource = downloadSource ?? candidate?.downloadSource ?? 'JOBS_MAPPING_FORM';
        if (!mapAsLead || isApplication) {
          if (candidateEntity?.jobId && (candidateEntity?.email || data.email)) {
            const count = await getSubmissionCount({
              candidateEmail: candidateEntity.email ?? data.email,
              jobId: candidateEntity.jobId,
            });
            if (count.data.count <= 0) {
              const response = await updateMapping(data, candidateEntity);
              if (response && response?.status != 'fail') {
                setNotification(success('Profile Updated'));
                updateFromPage('mapping');
                onCandidateUpdate(response);
              } else {
                setDisableSubmit(false);
                setNotification(failed(getMappingFailedNotification(response?.response)));
              }
            } else {
              setNotification(
                warning(
                  'This Candidate is already submitted for the job. Therefore you cannot update the mapping'
                )
              );
            }
          } else if (candidateEntity) {
            const response = await updateMapping(data, candidateEntity);
            if (response && response?.status != 'fail') {
              setNotification(success('Profile Updated'));
              updateFromPage('mapping');
              onCandidateUpdate(response);
            } else {
              setDisableSubmit(false);
              setNotification(failed(getMappingFailedNotification(response?.response)));
            }
          }
        } else {
          if (job?.id) {
            if (!isEmpty(data.email) || !isEmpty(data.socialLinks)) {
              const response = await saveMapping(data, candidateEntity, job);
              if (response && response.status == 'success') {
                setNotification(success('Candidate is mapped to selected job'));
                closeUpdatePanel();
                onCandidateUpdate(response.response);
              } else {
                if (
                  response?.response?.indexOf('There is no accepted work assignment for the job') >
                  -1
                ) {
                  setNotification(
                    failed(
                      `This assigned job is not been accepted by the recruiter. 
                    Please accept the job then map the candidate.`
                    )
                  );
                } else if (response?.responseCode === 400) {
                  setNotification(warning('Candidate is already mapped to this job'));
                  closeUpdatePanel();
                  job && cacheJob(job);
                  showProfile &&
                    openModalWithAction(response?.data.id, 'mapping', 'mapping', 'mapping');
                } else {
                  setNotification(failed('Mapping failed'));
                }
                setDisableSubmit(false);
              }
            } else {
              setDisableSubmit(false);
              setNotification(warning('Please enter email or social links'));
            }
          } else {
            setDisableSubmit(false);
            setNotification(warning('Please select job first'));
          }
        }
      } else {
        setNotification(warning('Please select recruiter actions or results'));
      }
    } else {
      setDisableSubmit(true);
      data.source = watch('source');
      data.downloadSource = downloadSource ?? candidate?.downloadSource ?? 'JOBS_MAPPING_FORM';
      if (!mapAsLead || isApplication) {
        if (candidateEntity?.jobId && (candidateEntity?.email || data.email)) {
          const count = await getSubmissionCount({
            candidateEmail: candidateEntity.email ?? data.email,
            jobId: candidateEntity.jobId,
          });
          if (count.data.count <= 0) {
            const response = await updateMapping(data, candidateEntity);
            if (response && response?.status != 'fail') {
              setNotification(success('Profile Updated'));
              updateFromPage('mapping');
              onCandidateUpdate(response);
            } else {
              setDisableSubmit(false);
              setNotification(failed(getMappingFailedNotification(response?.response)));
            }
          } else {
            setNotification(
              warning(
                'This Candidate is already submitted for the job. Therefore you cannot update the mapping'
              )
            );
          }
        } else if (candidateEntity) {
          const response = await updateMapping(data, candidateEntity);
          if (response && response?.status != 'fail') {
            setNotification(success('Profile Updated'));
            updateFromPage('mapping');
            onCandidateUpdate(response);
          } else {
            setDisableSubmit(false);
            setNotification(failed(getMappingFailedNotification(response?.response)));
          }
        }
      } else {
        if (job?.id) {
          if (!isEmpty(data.email) || !isEmpty(data.socialLinks)) {
            const response = await saveMapping(data, candidateEntity, job);
            if (response && response.status == 'success') {
              setNotification(success('Candidate is mapped to selected job'));
              closeUpdatePanel();
              onCandidateUpdate(response.response);
            } else {
              if (
                response?.response?.indexOf('There is no accepted work assignment for the job') > -1
              ) {
                setNotification(
                  failed(
                    `This assigned job is not been accepted by the recruiter. 
                  Please accept the job then map the candidate.`
                  )
                );
              } else if (response?.responseCode === 400) {
                setNotification(warning('Candidate is already mapped to this job'));
                closeUpdatePanel();
                job && cacheJob(job);
                showProfile &&
                  openModalWithAction(response?.data.id, 'mapping', 'mapping', 'mapping');
              } else {
                setNotification(failed('Mapping failed'));
              }
              setDisableSubmit(false);
            }
          } else {
            setDisableSubmit(false);
            setNotification(warning('Please enter email or social links'));
          }
        } else {
          setDisableSubmit(false);
          setNotification(warning('Please select job first'));
        }
      }
    }
  };

  const onUpload = async (file: File[], field: string) => {
    const url = await uploadFileToS3(file[0]);
    if (field === 'resume') {
      const alteredCandidate = candidateEntity ?? newMappingEntity();
      setCandidateResumes(
        alteredCandidate?.mappedCandidateResumes ? alteredCandidate?.mappedCandidateResumes : []
      );

      const alteredResumes = candidateResumes;
      alteredResumes.push({
        candidateMappingId: '',
        email: '',
        gitHubLink: '',
        id: '',
        linkedInLink: '',
        phone: '',
        pinCode: '',
        resumeFileOriginalName: file[0].name,
        resumeLocation: url,
        resumeUploadedOn: new Date(),
      });
      setCandidateResumes([...alteredResumes]);
      alteredCandidate.resumeLocation = url;
      alteredCandidate.resumeFileOriginalName = file[0].name;
      alteredCandidate.resumeUploadedOn = new Date();
      alteredCandidate.mappedCandidateResumes = alteredResumes;
      setAllowReset(false);
      setCandidateEntity({ ...alteredCandidate });
    }
    if (field === 'otherDocuments') {
      const alteredCandidate = candidateEntity ?? newMappingEntity();
      const alteredDocuments = [...otherDocument];

      alteredDocuments.push({
        documentLocation: url,
        originalName: file[0].name,
        tag: null,
        uploadedOn: new Date(),
      });
      setOtherDocument([...alteredDocuments]);
      alteredCandidate.otherDocuments = alteredDocuments;
      setCandidateEntity({ ...alteredCandidate });
    }
  };

  const resumeAction = (action: string, url: string | null) => {
    switch (action) {
      case 'view':
        if (url) {
          trackCV('view', url, cvViewSourcePage);
          setResumeURL(url);
        }
        setOpenCvView(true);
        break;
      case 'download':
        if (!isEmpty(url)) {
          trackCV('download', url, cvViewSourcePage);
          url && downloadCV(url);
        }
        break;
      case 'removeCandidate': {
        const alteredCandidate: ICandidate = candidateEntity
          ? { ...candidateEntity }
          : newMappingEntity();
        if (alteredCandidate?.mappedCandidateResumes) {
          const index = alteredCandidate?.mappedCandidateResumes?.findIndex(
            (resume) => resume.resumeLocation === url
          );
          if (index != -1) {
            alteredCandidate?.mappedCandidateResumes?.splice(index, 1);
            setCandidateResumes(alteredCandidate?.mappedCandidateResumes);
          }
          setCandidateEntity(alteredCandidate);
        }
        break;
      }
      case 'removeOtherDocument':
        if (candidateEntity) {
          const alteredCandidate = { ...candidateEntity };
          alteredCandidate.otherDocuments = alteredCandidate?.otherDocuments?.filter(
            (document) => document.documentLocation != url
          );
          setOtherDocument((prev) => {
            const newDocArray = prev.filter((prevDoc) => prevDoc.documentLocation != url);
            alteredCandidate.otherDocuments = newDocArray;
            return newDocArray;
          });
          setCandidateEntity({ ...alteredCandidate });
        }
        break;
    }
  };
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const amplitudeIntegration = () => {
    if (mapAsLead && isFrom === SourceType.JOBCRM_SMART_SOURCE_AI_SUGGESTIONS)
      amplitudeEventJobCrmSmartSourceAiMapAsLeadSave['Save'](loggedInUser);
    else if (mapAsLead && isFrom === SourceType.JOBCRM_SMART_SOURCE_SIMILAR_JOBS)
      amplitudeEventJobCrmSmartSourceSimilarJobsMapAsLeadSave['Save'](loggedInUser);
  };

  return (
    <WsDrawer
      open={openDrawer}
      style={{ zIndex: '1300' }}
      hideHeader={true}
      onClose={() => null}
      size="medium"
      hideOverflow={true}
    >
      {mapAsLead && (
        <ChatBoxHeader>
          <Grid container p={hideJobSelection ? 1 : 0}>
            <Grid item xs={3}>
              <Box mt={hideJobSelection ? 0 : 1}>
                <WsTypography
                  variant={WsTypoVariants.TITLE}
                  color={WsTypoColors.PRIMARY}
                  bold={true}
                >
                  {heading ?? 'Map as lead'}
                </WsTypography>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ float: 'right', mr: 2 }}>
                <Stack direction="row" spacing={3}>
                  {!hideJobSelection ? (
                    <WidgetJobManagement selectedJob={job} ss={12} isFromMapAsLead={true} />
                  ) : (
                    <WsTypography variant={WsTypoVariants.BODY1} bold={true}>
                      {`${job?.title} ( ${job?.jobCode} )`}
                    </WsTypography>
                  )}
                  <CloseIcon sx={{ cursor: 'pointer' }} onClick={closeUpdatePanel} />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </ChatBoxHeader>
      )}
      {!disableMapping && !duplicateMapping ? (
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            onKeyDown={(e) => e.code === 'Enter' && e.preventDefault()}
          >
            <Grid item xs={12} sx={{ maxHeight: '100vh', overflowY: 'auto' }}>
              <PersonalInfo
                candidate={candidateEntity}
                onUpload={(file: File[]) => onUpload(file, 'resume')}
                purpose="mapping"
                resumeAction={resumeAction}
                mappingFormSettings={mappingFormSettings}
                showTitle={showPersonalInfoSettings}
              />
              <Item>
                {mappingFormSettings?.recruiterActivity.show && (
                  <ControlledRecruiterActivity
                    name="recruiterActivity"
                    candidateInteractionMessagingPipeline={
                      pipeline.candidateInteractionMessagingPipeline
                    }
                    selectedActions={actions}
                    selectedResults={results}
                    candidateInteractionResultPipeline={pipeline.candidateInteractionResultPipeline}
                  />
                )}
                {mappingFormSettings?.notes.show && (
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <>
                        <TextareaAutosize
                          aria-label="notes"
                          onChange={onChange}
                          minRows={6}
                          value={value}
                          placeholder="Call/Message Notes"
                          style={{ width: '100%', marginTop: '12px' }}
                          required={mappingFormSettings?.notes.mandatory}
                        />
                        {error && <FormHelperText error>{error.message}</FormHelperText>}
                      </>
                    )}
                  />
                )}
              </Item>
              <LocationInfo
                purpose="mapping"
                mappingFormSettings={mappingFormSettings}
                showTitle={mappingFormSettings?.locations.show}
              />
              <WorkExpForm
                purpose="mapping"
                mappingFormSettings={mappingFormSettings}
                showTitle={showWorkExpSettings}
              />
              <JobPreference
                watch={watch}
                purpose="mapping"
                mappingFormSettings={mappingFormSettings}
                showTitle={showJobPreferenceSettings}
              />
              <SalaryDetails
                showTitle={showSalaryInfoSettings}
                purpose="mapping"
                job={job}
                mappingFormSettings={mappingFormSettings}
              />
              <AssessmentInfo
                showTitle={showAssessmentInfoSettings}
                purpose="mapping"
                mappingFormSettings={mappingFormSettings}
              />
              <DocumentInfo
                purpose="mapping"
                onUpload={(file: File[]) => onUpload(file, 'otherDocuments')}
                candidate={candidateEntity}
                resumeAction={resumeAction}
                mappingFormSettings={mappingFormSettings}
              />
              <Item sx={{ mb: 12 }}></Item>
              <ActionFooter>
                <Grid item container xs={5.7} justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <WsButton onClick={closeUpdatePanel}>Close</WsButton>
                    <WsButton
                      type="submit"
                      disabled={disableSubmit}
                      onClick={() => amplitudeIntegration()}
                    >
                      {mapAsLead ? 'Save' : 'Update'}
                    </WsButton>
                  </Stack>
                </Grid>
              </ActionFooter>
            </Grid>
          </form>
          {!isEmpty(candidateEntity?.mappedCandidateResumes) && (
            <CVViewModal
              open={openCvView}
              s3url={resumeURl}
              handleClose={() => setOpenCvView(false)}
            />
          )}
        </FormProvider>
      ) : (
        <Stack direction="column" spacing={2} mt={4} justifyContent="center" alignItems="center">
          <Item>
            <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.ERROR}>
              Duplicate Candidate
            </WsTypography>
            <WsTypography>
              {`This Candidate is already ${mapAsLead ? 'mapped' : 'submitted'} for the job ${
                job?.title
              } 
            (${job?.jobCode}). Therefore you cannot map this candidate)`}
            </WsTypography>
          </Item>
        </Stack>
      )}
    </WsDrawer>
  );
};

export default UpdateProfile;
